import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { GraduationCap, Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@jambushrushti/integrations/supabase/client";

export default function ScholarLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [fullName, setFullName] = useState("");
  const { login, signup, isAuthenticated, hasRole, user } = useAdminAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Wait for user data to be fully loaded before redirecting
    if (
      isAuthenticated &&
      user &&
      hasRole(["scholar", "admin", "superadmin"])
    ) {
      navigate("/scholar/dashboard", { replace: true });
    }
  }, [isAuthenticated, hasRole, user, navigate]);

  const assignScholarRole = async (userId: string) => {
    // Check if user already has a role
    const { data: existingRole } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();

    if (existingRole) {
      // Update to scholar if not already admin/superadmin
      if (!["admin", "superadmin"].includes(existingRole.role)) {
        await supabase
          .from("user_roles")
          .update({ role: "scholar" })
          .eq("user_id", userId);
      }
    } else {
      // Insert scholar role
      await supabase.from("user_roles").insert({
        user_id: userId,
        role: "scholar",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isSignUp) {
        const result = await signup(email, password, fullName);
        if (result.success) {
          toast({
            title: "Account created!",
            description:
              "Your scholar account has been created. You can now sign in.",
          });
          setIsSignUp(false);
        } else {
          toast({
            title: "Signup failed",
            description: result.error || "Please try again",
            variant: "destructive",
          });
        }
      } else {
        const result = await login(email, password);
        if (result.success) {
          toast({
            title: "Welcome to Scholar Portal!",
            description: "Successfully logged in",
          });
          // Redirect will happen via useEffect when user data is loaded
        } else {
          toast({
            title: "Login failed",
            description: result.error || "Invalid credentials",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setIsLoading(true);
    const demoEmail = "scholar@demo.com";
    const demoPassword = "scholar123";

    try {
      // Try to login first
      const loginResult = await login(demoEmail, demoPassword);

      if (loginResult.success) {
        // Get current user and ensure scholar role
        const {
          data: { user: currentUser },
        } = await supabase.auth.getUser();
        if (currentUser) {
          await assignScholarRole(currentUser.id);
        }
        toast({ title: "Welcome!", description: "Logged in as Demo Scholar" });
        navigate("/scholar/dashboard", { replace: true });
      } else {
        // Account doesn't exist, create it
        const { data: signupData, error: signupError } =
          await supabase.auth.signUp({
            email: demoEmail,
            password: demoPassword,
            options: {
              emailRedirectTo: `${window.location.origin}/`,
              data: { full_name: "Demo Scholar" },
            },
          });

        if (signupError) {
          toast({
            title: "Error",
            description: signupError.message,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        if (signupData.user) {
          // Assign scholar role
          await assignScholarRole(signupData.user.id);

          // Create profile
          await supabase.from("profiles").upsert({
            user_id: signupData.user.id,
            email: demoEmail,
            full_name: "Demo Scholar",
          });

          // Try login again
          const retryLogin = await login(demoEmail, demoPassword);
          if (retryLogin.success) {
            toast({
              title: "Demo account created!",
              description: "Welcome to Scholar Portal",
            });
            navigate("/scholar/dashboard", { replace: true });
          }
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to login with demo account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="lotus-pattern flex min-h-screen items-center justify-center p-4"
      style={{ backgroundColor: "#FAF7F2" }}
    >
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(210, 129, 29, 0.08)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(45, 42, 38, 0.05)" }}
        />
      </div>

      <div className="relative w-full max-w-md">
        {/* Back Link */}
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <Card className="border-border shadow-2xl">
          <CardHeader className="pb-2 text-center">
            <div className="bg-orange/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl">
              <GraduationCap className="text-orange h-8 w-8" />
            </div>
            <CardTitle className="font-heading text-2xl text-primary">
              Scholar Portal
            </CardTitle>
            <CardDescription>
              {isSignUp
                ? "Create your scholar account"
                : "Sign in to access your research portal"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Dr. John Doe"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    required={isSignUp}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="scholar@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isSignUp ? "Creating Account..." : "Signing In..."}
                  </span>
                ) : isSignUp ? (
                  "Create Account"
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button
                variant="link"
                className="text-muted-foreground hover:text-primary"
                onClick={() => setIsSignUp(!isSignUp)}
              >
                {isSignUp
                  ? "Already have an account? Sign in"
                  : "New scholar? Create an account"}
              </Button>
            </div>

            {/* Demo Login Section */}
            <div className="bg-orange/5 border-orange/20 mt-4 rounded-xl border p-4">
              <p className="text-orange mb-2 text-center text-sm font-medium">
                Demo Scholar Login
              </p>
              <div className="mb-3 space-y-1 text-xs text-muted-foreground">
                <p>
                  <span className="font-medium">Email:</span> scholar@demo.com
                </p>
                <p>
                  <span className="font-medium">Password:</span> scholar123
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-orange/30 text-orange hover:bg-orange/10 w-full text-sm"
                onClick={handleDemoLogin}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Logging in...
                  </span>
                ) : (
                  "Use Demo Login"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
